import React, { useEffect, useState } from 'react';

import { useGetOne } from 'react-admin';

import {
  Card,
  CardContent,
  Box,
  CardHeader,
  LinearProgress,
  Typography,
  Divider,
} from '@mui/material';

import ReactApexChart from 'react-apexcharts';
import { isEmpty } from 'lodash';
import SelectInputButton from '../../layout/inputFields/SelectInputButton';
import theme from '../../../theme';
import useHoverSeriesUpdateLabel from '../../../hooks/useHoverSeriesUpdateLabel';

const useGetGraphData = (
  data: {
    numberRequestByRecommendation: {
      review?: number;
      proceed?: number;
      block?: number;
    };
  },
  chartRef: React.RefObject<ReactApexChart>,
) => {
  const [hasData, setHasData] = useState(false);
  const [options, setOptions] = useState<ReactApexChart['props']['options']>();
  const [series, setSeries] = useState<ReactApexChart['props']['series']>();

  const {
    getTotalOptions,
    attachLegendHoverEvents,
  } = useHoverSeriesUpdateLabel({ chartRef });

  useEffect(() => {
    if (!data) return;

    const colors = ['#DE2B2B', theme.palette.success.main, '#F3B61F'];

    const labels = ['Block', 'Proceed', 'Review'];

    const newSeries = [
      data.numberRequestByRecommendation.block ?? 0,
      data.numberRequestByRecommendation.proceed ?? 0,
      data.numberRequestByRecommendation.review ?? 0,
    ] satisfies ReactApexChart['props']['series'];

    const totalOptions = getTotalOptions({
      series: newSeries,
      colors,
      labels,
      totalColor: theme.palette.primary.main,
      totalLabel: 'Total requests',
    });

    const newOptions = {
      colors,
      labels,
      chart: {
        redrawOnParentResize: true,
        height: 400,
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
        events: {
          mounted: attachLegendHoverEvents,
          updated: attachLegendHoverEvents,
        },
      },
      legend: {
        position: 'bottom',
        show: !isEmpty(data.numberRequestByRecommendation),
      },
      plotOptions: {
        pie: {
          donut: {
            labels: {
              show: true,
              total: {
                show: true,
                ...totalOptions,
              },
            },
          },
        },
      },
    } satisfies ReactApexChart['props']['options'];

    setHasData(true);
    setOptions(newOptions);
    setSeries(newSeries);
  }, [attachLegendHoverEvents, data, getTotalOptions]);

  return {
    hasData,
    options,
    series,
  };
};

const RequestAnalytics = () => {
  const [period, setPeriod] = useState('30');
  const { data, isLoading } = useGetOne('dashboard', { id: `requests/${period}` });

  const chartRef = React.useRef<ReactApexChart>(null);
  const { hasData, options, series } = useGetGraphData(data, chartRef);

  return (
    <Card
      variant="outlined"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'stretch',
        height: '100%',
      }}
    >
      <CardHeader
        title="Requests"
        action={(
          <SelectInputButton
            choices={[
              { id: '1', name: 'Last day' },
              { id: '7', name: 'Last week' },
              { id: '30', name: 'Last month' },
              { id: '90', name: 'Last quarter' },
              { id: '365', name: 'Last year' },
              { id: '99999', name: 'All time' },
            ]}
            value={period}
            setValue={setPeriod}
          />
        )}
      />
      <Divider />
      <CardContent
        sx={{
          flexGrow: 1,
        }}
      >
        {isLoading && <LinearProgress /> }
        {hasData && <ReactApexChart ref={chartRef} options={options} series={series} type="donut" height={300} />}
        {(!isLoading && !hasData) && (
          <Box margin={10} width="100%">
            <Typography align="center"> No requests available </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default RequestAnalytics;
